// @flow
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/styles';

import {colors} from '../../../../../assets/styles/colors';
import {
  Grid,
  Hidden,
  Radio,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@material-ui/core';
import ColorPalette from '../ColorPalette/ColorPalette';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {
  CheckCircle,
  RadioButtonUnchecked,
  ArrowForward,
  ArrowBack,
} from '@material-ui/icons';

import {
  ALLOWED_PRIMARY_COLORS,
  ALLOWED_SECONDARY_COLORS,
  DEFAULT_COLOR_OPTIONS,
} from '../../api';
import {usePrevious} from '../../../../../utils/react';
import {useIsFeatureInTier} from '../../../../../utils/component';
import ColorVisualizer from '../ColorVisualizer/ColorVisualizer';

const useStyles = makeStyles(theme => ({
  headerSelection: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  noSelected: {
    color: colors.grey700,
  },
  btnSelect: {
    width: '100%',
    height: '100%',
    '& span': {
      height: '100%',
    },
    border: '1px solid #E8E8ED',
    borderRadius: '8px',
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'flex-start',
  },
  btnContent: {
    width: '100%',
    position: 'relative',
  },
  checkedWrap: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
  },
  containerDots: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  dot: {
    width: '0.8rem',
    height: '0.8rem',
    borderRadius: '50%',
    backgroundColor: colors.grey300,
  },
  dotSelected: {
    backgroundColor: colors.grey500,
  },
  btnDot: {
    width: 'fit-content',
    '& .MuiButton-label': {
      width: 'fit-content',
    },
  },
  rowCustomizeColor: {
    border: '1.25px solid #E8E8ED',
    borderRadius: 8,
    padding: theme.spacing(2),
  },
  principalText: {
    color: colors.grey900,
    fontWeight: 600,
    fontSize: 14,
    marginBottom: theme.spacing(1.25),
  },
  arrowBack: {
    position: 'absolute',
    left: 0,
    zIndex: 1,
  },
  arrowForward: {
    position: 'absolute',
    right: 0,
    zIndex: 1,
  },
  infoTooltip: {
    color: colors.purple3,
    backgroundColor: colors.lightPurple,
    borderRadius: '8px',
    textAlign: 'center',
    maxWidth: 240,
  },
  errorText: {
    marginLeft: theme.spacing(1),
    fontSize: 16,
    [theme.breakpoints.up('md')]: {
      fontSize: 14,
    },
    color: '#f44336',
  },
}));

type Props = {
  showAdvancedConfig?: boolean,
  missingFontColor?: boolean,
  errors?: {[key: string]: any},
  register: (val: any) => void,
  setValue: (name: string, value: any) => void,
  watch: any,
  colorSettingOption: string,
  setColorSettingOption: (val: string) => void,
  colorOptionSelected?: number,
  setColorOptionSelected: (id: number) => void,
  primaryFontColor?: string,
  setPrimaryFontColor: (val: string) => void,
  secondaryFontColor?: string,
  setSecondaryFontColor: (val: string) => void,
};

export default function ColorsSection(props: Props) {
  const {
    showAdvancedConfig = true,
    missingFontColor,
    errors,
    watch,
    setValue,
    register,
    colorSettingOption,
    setColorSettingOption,
    colorOptionSelected,
    setColorOptionSelected,
    primaryFontColor,
    setPrimaryFontColor,
    secondaryFontColor,
    setSecondaryFontColor,
  } = props;

  const {primaryBackgroundColor, secondaryBackgroundColor} = watch();
  const classes = useStyles();

  const [currentSlider, setCurrentSlider] = useState(0);
  const prevColorOptionSelected = usePrevious(colorOptionSelected);
  const isMobile = useMediaQuery('(max-width:600px)');
  const hasAdvancedColorSetting = useIsFeatureInTier(
    'ADVANCED_COLOR_CONFIGURATION'
  );

  useEffect(() => {
    if (!prevColorOptionSelected && typeof colorOptionSelected === 'number') {
      const r = Math.floor(colorOptionSelected / 2);
      setCurrentSlider(r);
    }
  }, [colorOptionSelected, prevColorOptionSelected]);

  const handleViewSlider = (id: number) => {
    setCurrentSlider(id);
  };
  const handleColorSettingOption = e => {
    setColorSettingOption(e.target.value);
  };

  const handleNextSlider = () => {
    const length = DEFAULT_COLOR_OPTIONS.length / 2;
    if (currentSlider < length - 1) {
      setCurrentSlider(currentSlider + 1);
    } else {
      setCurrentSlider(0);
    }
  };

  const handlePrevSlider = () => {
    const length = DEFAULT_COLOR_OPTIONS.length / 2;
    if (currentSlider > 0) {
      setCurrentSlider(currentSlider - 1);
    } else {
      setCurrentSlider(length - 1);
    }
  };

  const checkedCheckbox = (
    <CheckCircle color="primary" style={{fontSize: '1.75rem'}} />
  );
  const uncheckedCheckbox = (
    <RadioButtonUnchecked style={{color: '#D4D4DD', fontSize: '1.75rem'}} />
  );

  const advancedColorRadioButton = (
    <div>
      <Radio
        id="opt-1"
        color="primary"
        type="radio"
        value="1"
        checked={colorSettingOption === '1'}
        onChange={handleColorSettingOption}
        disabled={!hasAdvancedColorSetting}
      />
      <label
        htmlFor="opt-1"
        className={`${classes.radioButtonLabel} ${
          colorSettingOption !== '1' ? classes.noSelected : ''
        }`}
      >
        Personalizar colores
      </label>
    </div>
  );

  const tooltipAdvancedColor = !hasAdvancedColorSetting ? (
    <Tooltip
      placement={isMobile ? 'left' : 'right'}
      disableFocusListener
      disableTouchListener
      leaveDelay={300}
      title="Disponible en PRO+"
      PopperProps={{
        disablePortal: true,
      }}
      classes={{tooltip: classes.infoTooltip}}
    >
      {advancedColorRadioButton}
    </Tooltip>
  ) : (
    <>{advancedColorRadioButton}</>
  );

  const fontsContent = DEFAULT_COLOR_OPTIONS.map((co, idx) => (
    <Grid key={'color-opt-' + idx} item xs={6}>
      <CustomButton
        className={classes.btnSelect}
        onClick={() => setColorOptionSelected(idx)}
      >
        <div className={classes.btnContent}>
          <div className={classes.checkedWrap}>
            {colorOptionSelected === idx ? checkedCheckbox : uncheckedCheckbox}
          </div>
          <ColorPalette
            primaryColor={co.primaryBackgroundColor}
            secondaryColor={co.secondaryBackgroundColor}
          />
        </div>
      </CustomButton>
    </Grid>
  ));

  const currentColorContent = (idx: number) => {
    const idxLeft = idx * 2;
    const idxRight = (idx + 1) * 2;
    return (
      <div className={classes.wrapOptionsColor}>
        <Grid container spacing={1}>
          {fontsContent.slice(idxLeft, idxRight)}
        </Grid>
      </div>
    );
  };

  const buildListItems = () => {
    return DEFAULT_COLOR_OPTIONS.map((co, idx) => (
      <Grid key={'color-option' + idx} item xs={6} sm={6} lg={4}>
        <CustomButton
          className={classes.btnSelect}
          onClick={() => setColorOptionSelected(idx)}
        >
          <div className={classes.btnContent}>
            <div className={classes.checkedWrap}>
              {colorOptionSelected === idx
                ? checkedCheckbox
                : uncheckedCheckbox}
            </div>
            <ColorPalette
              primaryColor={co.primaryBackgroundColor}
              secondaryColor={co.secondaryBackgroundColor}
            />
          </div>
        </CustomButton>
      </Grid>
    ));
  };

  return (
    <div>
      <div className={classes.headerSelection}>
        <div>
          <Radio
            id="opt-0"
            color="primary"
            type="radio"
            value="0"
            checked={colorSettingOption === '0'}
            onChange={handleColorSettingOption}
          />
          <label
            htmlFor="opt-0"
            className={`${classes.radioButtonLabel} ${
              colorSettingOption !== '0' ? classes.noSelected : ''
            }`}
          >
            Temas predeterminados
          </label>
        </div>
        {!!showAdvancedConfig && tooltipAdvancedColor}
      </div>
      <div>
        {colorSettingOption === '0' ? (
          <>
            <Hidden xsDown>
              <Grid container spacing={2}>
                {buildListItems()}
              </Grid>
            </Hidden>
            <Hidden smUp>
              {currentColorContent(currentSlider)}
              <div className={classes.containerDots}>
                <IconButton
                  className={classes.arrowBack}
                  color="primary"
                  onClick={handlePrevSlider}
                >
                  <ArrowBack />
                </IconButton>
                <div className={classes.dots}>
                  {[0, 1, 2].map((to, idx) => (
                    <CustomButton
                      key={idx}
                      className={classes.btnDot}
                      onClick={() => handleViewSlider(idx)}
                    >
                      <div
                        className={`${classes.dot} ${
                          currentSlider === idx ? classes.dotSelected : ''
                        }`}
                      />
                    </CustomButton>
                  ))}
                </div>
                <IconButton
                  className={classes.arrowForward}
                  color="primary"
                  onClick={handleNextSlider}
                >
                  <ArrowForward />
                </IconButton>
              </div>
            </Hidden>
          </>
        ) : (
          <Grid container spacing={2}>
            {missingFontColor && (
              <Typography className={classes.errorText}>
                Escoge el color de los textos.
              </Typography>
            )}
            <Grid item xs={12}>
              <Typography className={classes.principalText}>
                Color Principal
              </Typography>

              <div className={classes.rowCustomizeColor}>
                <ColorVisualizer
                  errors={errors}
                  setValue={setValue}
                  name="primaryBackgroundColor"
                  register={register}
                  backgroundColor={primaryBackgroundColor}
                  arrayTextColors={ALLOWED_PRIMARY_COLORS}
                  defaultTextColor={primaryFontColor}
                  textColor={primaryFontColor}
                  setTextColor={setPrimaryFontColor}
                />
              </div>
            </Grid>
            <Grid item xs={12}>
              <Typography className={classes.principalText}>
                Color Secundario
              </Typography>
              <div className={classes.rowCustomizeColor}>
                <ColorVisualizer
                  errors={errors}
                  setValue={setValue}
                  name="secondaryBackgroundColor"
                  register={register}
                  backgroundColor={secondaryBackgroundColor}
                  arrayTextColors={ALLOWED_SECONDARY_COLORS}
                  defaultTextColor={secondaryFontColor}
                  textColor={secondaryFontColor}
                  setTextColor={setSecondaryFontColor}
                />
              </div>
            </Grid>
          </Grid>
        )}
      </div>
    </div>
  );
}

ColorsSection.propTypes = {
  showAdvancedConfig: PropTypes.bool,
  missingFontColor: PropTypes.bool,
  errors: PropTypes.object,
  register: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  colorSettingOption: PropTypes.string.isRequired,
  setColorSettingOption: PropTypes.func.isRequired,
  colorOptionSelected: PropTypes.number,
  setColorOptionSelected: PropTypes.func.isRequired,
  primaryFontColor: PropTypes.string,
  setPrimaryFontColor: PropTypes.func.isRequired,
  secondaryFontColor: PropTypes.string,
  setSecondaryFontColor: PropTypes.func.isRequired,
};
